import React from 'react';
import { useTranslation } from 'react-i18next';

const Pagination = ({
  currentPage = 1,
  totalItems = 0,
  pageSize = 25,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100],
  className = ''
}) => {
  const { t } = useTranslation();

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const validPage = Math.min(Math.max(1, currentPage), totalPages);

  const startItem = totalItems === 0 ? 0 : (validPage - 1) * pageSize + 1;
  const endItem = Math.min(validPage * pageSize, totalItems);

  const handlePrev = () => {
    if (validPage > 1 && onPageChange) {
      onPageChange(validPage - 1);
    }
  };

  const handleNext = () => {
    if (validPage < totalPages && onPageChange) {
      onPageChange(validPage + 1);
    }
  };

  const handlePageClick = (page) => {
    if (page !== validPage && page >= 1 && page <= totalPages && onPageChange) {
      onPageChange(page);
    }
  };

  // Generate page numbers with ellipsis for large page counts
  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages = [];
    if (validPage <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push('...');
      pages.push(totalPages);
    } else if (validPage >= totalPages - 3) {
      pages.push(1);
      pages.push('...');
      for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push('...');
      pages.push(validPage - 1);
      pages.push(validPage);
      pages.push(validPage + 1);
      pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const trans = t('showing-items', {
    start: startItem,
    end: endItem,
    total: totalItems
  });
  const showingText = (!trans || trans === 'showing-items')
    ? `Visar ${startItem}–${endItem} av ${totalItems}`
    : trans;

  const prevText = (!t('previous') || t('previous') === 'previous') ? 'Föregående' : t('previous');
  const nextText = (!t('next-page') || t('next-page') === 'next-page') ? 'Nästa' : t('next-page');
  const rowsText = (!t('rows-per-page') || t('rows-per-page') === 'rows-per-page') ? 'Rader per sida:' : t('rows-per-page');

  return (
    <div className={`pagination-container ${className}`.trim()}>
      <div className="pagination-summary">
        {showingText}
      </div>

      <div className="pagination-controls">
        <button
          type="button"
          className="pagination-button pagination-prev"
          onClick={handlePrev}
          disabled={validPage <= 1}
          aria-label={prevText}
        >
          {prevText}
        </button>

        <div className="pagination-pages">
          {getPageNumbers().map((page, idx) => {
            if (page === '...') {
              return (
                <span key={`ellipsis-${idx}`} className="pagination-ellipsis">
                  …
                </span>
              );
            }
            return (
              <button
                key={`page-${page}`}
                type="button"
                className={`pagination-page-button ${page === validPage ? 'active' : ''}`}
                onClick={() => handlePageClick(page)}
                aria-current={page === validPage ? 'page' : undefined}
                aria-label={`Sida ${page}`}
              >
                {page}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className="pagination-button pagination-next"
          onClick={handleNext}
          disabled={validPage >= totalPages}
          aria-label={nextText}
        >
          {nextText}
        </button>
      </div>

      {onPageSizeChange && pageSizeOptions && pageSizeOptions.length > 0 && (
        <div className="pagination-page-size">
          <label htmlFor="pagination-size-select" className="pagination-size-label">
            {rowsText}
          </label>
          <select
            id="pagination-size-select"
            className="pagination-size-select"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {pageSizeOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default Pagination;
export { Pagination };
